import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { confirmLscBroadcasterSide, LscApiError } from '../api';
import { getLscEntitlementErrorGuidance } from '../entitlementBillingErrors';
import { ReportIssueTrigger } from './support/ReportIssueTrigger';

export type BroadcasterSideConfirmationPanelProps = {
  leagueId: string;
  homeTeamLabel?: string | null;
  awayTeamLabel?: string | null;
  twitchUserId?: string | null;
  streamId?: string | null;
  competitionMatchId?: string | null;
  needsAttentionItemKey?: string | null;
  onConfirmed?: () => void | Promise<void>;
  testIdPrefix?: string;
};

export function mapBroadcasterSideConfirmationError(error: unknown): string {
  if (error instanceof LscApiError) {
    const entitlement = getLscEntitlementErrorGuidance(error);
    if (entitlement) {
      return entitlement.message;
    }
    switch (error.code) {
      case 'strong_side_conflict':
        return 'This side conflict needs additional review before it can be confirmed.';
      case 'side_not_reviewable':
        return 'This game is not waiting for side confirmation right now.';
      case 'cross_league_target':
        return 'This stream does not belong to the selected league.';
      case 'invalid_broadcaster_side':
        return 'Choose Home or Away before confirming.';
      case 'live_truth_target_not_found':
        return 'This game could not be found for side confirmation. Refresh Needs Attention and try again.';
      case 'archive_locked':
        return 'This game is already finalized in the archive workflow and cannot be changed here.';
      case 'unauthorized':
      case 'forbidden':
        return 'You do not have permission to confirm broadcaster side.';
      default:
        break;
    }
    if (error.status === 401 || error.status === 403) {
      return 'You do not have permission to confirm broadcaster side.';
    }
  }
  return 'Broadcaster side could not be confirmed. Try again in a moment.';
}

export function formatBroadcasterSideSuccessMessage(
  side: 'home' | 'away',
  homeTeamLabel?: string | null,
  awayTeamLabel?: string | null,
  remainingBlockers?: string[] | null
): string {
  const label = side === 'home' ? homeTeamLabel?.trim() || 'Home' : awayTeamLabel?.trim() || 'Away';
  const base = `Broadcaster side confirmed: ${label}.`;
  if (remainingBlockers?.includes('stream_still_live')) {
    return `${base} VOD handoff will continue after the stream ends if other checks pass.`;
  }
  if (remainingBlockers?.length) {
    return `${base} Some follow-up checks are still pending.`;
  }
  return base;
}

const BroadcasterSideConfirmationPanel: React.FC<BroadcasterSideConfirmationPanelProps> = ({
  leagueId,
  homeTeamLabel,
  awayTeamLabel,
  twitchUserId,
  streamId,
  competitionMatchId,
  needsAttentionItemKey,
  onConfirmed,
  testIdPrefix = 'broadcaster-side-confirmation',
}) => {
  const [submittingSide, setSubmittingSide] = useState<'home' | 'away' | null>(null);
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const canSubmit = Boolean(
    leagueId && (twitchUserId?.trim() || needsAttentionItemKey?.trim())
  );

  const handleConfirm = async (side: 'home' | 'away') => {
    if (!canSubmit || submittingSide) return;
    setSubmittingSide(side);
    setMessage(null);
    try {
      const result = await confirmLscBroadcasterSide(leagueId, {
        broadcaster_side: side,
        twitch_user_id: twitchUserId?.trim() || undefined,
        stream_id: streamId?.trim() || undefined,
        competition_match_id: competitionMatchId?.trim() || undefined,
        needs_attention_item_key: needsAttentionItemKey?.trim() || undefined,
      });
      setMessage({
        kind: 'success',
        text: formatBroadcasterSideSuccessMessage(
          side,
          homeTeamLabel,
          awayTeamLabel,
          result.remaining_blockers
        ),
      });
      await onConfirmed?.();
    } catch (error) {
      setMessage({
        kind: 'error',
        text: mapBroadcasterSideConfirmationError(error),
      });
    } finally {
      setSubmittingSide(null);
    }
  };

  return (
    <div
      className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-3"
      data-testid={testIdPrefix}
    >
      <p className="text-sm text-amber-100/90" data-testid={`${testIdPrefix}-prompt`}>
        Confirm which side this broadcast represented.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-secondary text-sm disabled:opacity-50"
          disabled={!canSubmit || submittingSide != null}
          onClick={() => {
            void handleConfirm('home');
          }}
          data-testid={`${testIdPrefix}-home`}
        >
          {submittingSide === 'home' ? (
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" aria-hidden />
          ) : null}
          Home{homeTeamLabel?.trim() ? `: ${homeTeamLabel.trim()}` : ''}
        </button>
        <button
          type="button"
          className="btn-secondary text-sm disabled:opacity-50"
          disabled={!canSubmit || submittingSide != null}
          onClick={() => {
            void handleConfirm('away');
          }}
          data-testid={`${testIdPrefix}-away`}
        >
          {submittingSide === 'away' ? (
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" aria-hidden />
          ) : null}
          Away{awayTeamLabel?.trim() ? `: ${awayTeamLabel.trim()}` : ''}
        </button>
      </div>
      {message ? (
        <div className="space-y-2">
          <p
            className={`text-xs ${message.kind === 'success' ? 'text-emerald-200' : 'text-red-300'}`}
            data-testid={`${testIdPrefix}-message`}
          >
            {message.text}
          </p>
          {message.kind === 'error' ? (
            <ReportIssueTrigger
              variant="inline"
              testId={`${testIdPrefix}-report-issue`}
              prefill={{
                leagueId,
                surface: 'needs_attention_side_confirmation',
                category: 'live_truth',
                title: 'Live side confirmation issue',
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default BroadcasterSideConfirmationPanel;
